import * as React from 'react';
import Badge from 'antd/lib/badge';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { LocalizedMetadata, ReleaseStepMetadataData } from '@/types';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { statusColorForLocalizationStatus } from '@/helpers/displayHelpers';
import { ArchetypeSpecificStepRunSummaryPanelProps, StepRunSummaryDetailItem, StepRunSummaryDetailSeparator } from '.';

const shortLocaleCode = (locale: string): string => {
    const [lang, region] = locale.split('-');
    return (region ?? lang).toUpperCase();
};

interface LocaleStatusDotProps {
    localization: LocalizedMetadata;
    isPrimary: boolean;
}

const LocaleStatusDot: React.FC<LocaleStatusDotProps> = ({ localization, isPrimary }) => {
    const { locale, displayLocale, status, whatsNew } = localization;
    const color = statusColorForLocalizationStatus(status);
    const code = shortLocaleCode(locale);
    const releaseNotesText = whatsNew || 'No release notes added.';

    return (
        <Tooltip title={releaseNotesText}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '6px 10px',
                    alignSelf: 'stretch',
                    cursor: 'default',
                    backgroundColor: isPrimary ? antGrey.grey2 : antGrey.grey3,
                }}
            >
                <Badge color={color} />
                {isPrimary && (
                    <Text
                        style={{
                            ...titleStyle,
                            fontSize: 11,
                            color: antGrey.grey8,
                        }}
                    >
                        {displayLocale.toUpperCase()}
                    </Text>
                )}
                <Text
                    style={{
                        ...titleStyle,
                        fontSize: 11,
                        color: antGrey.grey7,
                    }}
                >
                    {code}
                </Text>
            </div>
        </Tooltip>
    );
};

export const MetadataStepRunSummaryPanel: React.FC<ArchetypeSpecificStepRunSummaryPanelProps> = ({ stepRun }) => {
    const stepData = {
        ...stepRun.data.summaryData,
        ...stepRun.data.complementaryData,
    } as ReleaseStepMetadataData;

    const { isApproved, localizations, primaryLocale } = stepData;

    const primaryLocalization = localizations?.find((l) => l.locale === primaryLocale) ?? localizations?.[0];
    const whatsNewText = primaryLocalization?.whatsNew;

    const approvalTag = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tag
                style={{
                    ...titleStyle,
                    fontSize: 10,
                    lineHeight: '22px',
                    margin: 0,
                    color: isApproved ? runwayColors.statusGreen : antGrey.grey6,
                    background: runwayColors.white,
                    border: `1px solid ${isApproved ? runwayColors.statusGreen : antGrey.grey4}`,
                }}
            >
                {isApproved ? 'APPROVED' : 'NOT APPROVED'}
            </Tag>
        </div>
    );

    const subtleBgStyle: React.CSSProperties = {
        backgroundColor: antGrey.grey2,
        borderRadius: 6,
        padding: '6px 10px',
        overflow: 'hidden',
    };

    const releaseNotesBody = (
        <div style={subtleBgStyle}>
            {whatsNewText ?
                <Text style={{ color: antGrey.grey8 }} ellipsis={{ tooltip: whatsNewText }}>
                    {whatsNewText}
                </Text>
            :   <Text style={{ color: antGrey.grey6, fontStyle: 'italic' }}>No release notes added.</Text>}
        </div>
    );

    const localesBody = (
        <div
            style={{
                display: 'flex',
                borderRadius: 6,
                overflow: 'hidden',
            }}
        >
            {localizations?.map((localization) => (
                <LocaleStatusDot
                    key={localization.locale}
                    localization={localization}
                    isPrimary={localization.locale === primaryLocale}
                />
            ))}
        </div>
    );

    return (
        <div
            style={{
                width: '100%',
                backgroundColor: runwayColors.white,
                display: 'flex',
                flexWrap: 'wrap',
                flex: 1,
                gap: 1,
                borderRadius: 8,
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: antGrey.grey4,
            }}
        >
            <StepRunSummaryDetailItem header="APPROVAL STATUS" body={approvalTag} />
            <StepRunSummaryDetailSeparator />
            <StepRunSummaryDetailItem header="RELEASE NOTES" body={releaseNotesBody} />
            <StepRunSummaryDetailSeparator />
            <StepRunSummaryDetailItem header="METADATA STATUS" body={localesBody} />
        </div>
    );
};
